import React, { useCallback, useMemo, FC, SyntheticEvent, ChangeEvent, FocusEvent } from 'react';
import { CommonFieldProps, StylableComponentProps, InputProps } from '@cld/common-definitions';
import { Radio as BaseRadio } from './Radio';
import { RadioGroupContext, useRadioGroupContext } from './context';

export type RadioGroupProps = CommonFieldProps<string, SyntheticEvent<HTMLInputElement>>;
export interface RadioGroupRadioProps extends CommonFieldProps<string, SyntheticEvent<HTMLInputElement>>, StylableComponentProps {
  /** Id of the HTML input. */
  id?: string;
  /** Props passed directly to HTML input inside **(overriding props already handled by abstraction)**. */
  inputProps?: InputProps;
}

/**
 * RadioGroup is component that should be used as wrapper for multiple Radios because it passes down to RadioGroup.Radio
 * components any props set on this component, for example "name"/"onChange" is shared through all of its Radio children
 * There is GroupLayout component in @cld/form to position vertically Group of radios
 */
export const RadioGroupCompoundParent: FC<RadioGroupProps> = ({ children, ...restProps }) => {
  const contextValue = useMemo(() => ({ ...restProps }), [restProps]);
  return <RadioGroupContext.Provider value={contextValue}>{children}</RadioGroupContext.Provider>;
};

const Radio: FC<RadioGroupRadioProps> = ({ id, value, isDisabled, inputProps, onChange, onFocus, onBlur }) => {
  const radioGroupContextValue = useRadioGroupContext();
  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value, e);
    radioGroupContextValue.onChange?.(e.target.value, e);
  }, [radioGroupContextValue.onChange, onChange]);
  const handleFocus = useCallback((e: FocusEvent<HTMLInputElement>) => {
    onFocus?.(e);
    radioGroupContextValue.onFocus?.(e);
  }, [radioGroupContextValue.onFocus, onFocus]);
  const handleBlur = useCallback((e: FocusEvent<HTMLInputElement>) => {
    onBlur?.(e);
    radioGroupContextValue.onBlur?.(e);
  }, [radioGroupContextValue.onBlur, onBlur]);
  const isChecked = radioGroupContextValue.value === value;

  return (
    <BaseRadio
      {...radioGroupContextValue}
      {...inputProps}
      id={id}
      value={value}
      checked={isChecked}
      aria-checked={isChecked}
      disabled={isDisabled}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

export const RadioGroup = Object.assign(RadioGroupCompoundParent, { Radio });
